import { useQuery } from '@tanstack/react-query';

import { useCapability } from '../../../hooks/use-capability';
import { api } from '../../../lib/api-client';

import type { ConversationDetail } from '../../../lib/types';

/** Party envolvida num ConflictAlert (r7b §32.4 ADR-117). */
export interface ConflictAlertParty {
  /** Tipo de entidade — canonical enum Brief 2A r7b Fatia 1. */
  type: 'Person' | 'Bond' | 'User';
  name: string;
  role: string;
}

export interface ActiveConflictAlert {
  alertId: string;
  category: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  bondsInvolved: string[];
  createdAt: string;
  /**
   * Parties envolvidas — exposto canonicamente por backend (Brief 2A r7b
   * Fatia 1). Sempre presente; vazio quando alert não tem bonds nem
   * participants.
   */
  parties: ConflictAlertParty[];
  /**
   * Contexto de detecção em linguagem natural — canonical para cat A
   * (template determinístico) e cat B-E (protection-agent — Fatia 11+).
   */
  detectionContext: string | null;
  /**
   * Rationale da IA para a severity — só populado em cat B-E
   * (protection-agent). Cat A determinístico → null.
   */
  rationale: string | null;
}

export interface ConflictAlertsState {
  status: 'idle' | 'loading' | 'ready' | 'error' | 'disabled';
  /** Lista de alerts `open` que envolvem o `bondId` da conversa em foco. */
  alerts: ActiveConflictAlert[];
}

const EMPTY: ConflictAlertsState = { status: 'idle', alerts: [] };

/**
 * useConflictAlerts — PR-B.2 (Item 89).
 *
 * Carrega `ConflictAlert`s `open` do tenant e filtra client-side pelos que
 * envolvem o `bondId` da conversa em foco (`bondsInvolved.includes(bondId)`).
 *
 * **Não substitui** o canal canônico Fatia 7 (`AISuggestionService` emite
 * `ai.suggestion.blocked-by-conflict` / `warning` via WS quando há sugestão
 * IA pendente — esse caminho preenche o `ConflictWarningBanner` via
 * `useAISuggestion`). Este hook complementa: quando a sugestão IA não está
 * ativa (atendente em redação manual, modo H_a sem trigger recente), o
 * banner ainda pode aparecer com base no estado da tabela `conflict_alerts`.
 *
 * **Capability gate canônico:** `conflict-of-interest-detection` (Pro+).
 * Sem capability → estado `disabled` (zero requisição).
 *
 * **Sem bondId** (NetComet típico — anônimo sem bond) → estado `disabled`
 * (nenhum alert pode envolver a conversa).
 *
 * Pattern segue `useQualitySignals` (Fatia 8) sem WS subscribe — alerts
 * mudam em frequência baixíssima vs presence/messages; refetch é trigger
 * suficiente.
 */
export function useConflictAlerts(conversation: ConversationDetail): ConflictAlertsState {
  const hasCapability = useCapability('conflict-of-interest-detection');
  const bondId = conversation.bondId;
  const enabled = hasCapability && bondId !== null;

  const queryResult = useQuery({
    queryKey: ['conflict-alerts', bondId],
    enabled,
    queryFn: () => api.listConflictAlerts({ status: 'open' }),
    // Alerts mudam em baixa frequência — staleTime alto evita refetch desnecessário.
    staleTime: 60_000,
  });

  if (!enabled) return { ...EMPTY, status: 'disabled' };
  if (queryResult.isError) return { ...EMPTY, status: 'error' };
  if (queryResult.isLoading) return { ...EMPTY, status: 'loading' };

  const all = queryResult.data?.data ?? [];
  const alerts: ActiveConflictAlert[] = all
    .filter((a) => bondId !== null && a.bondsInvolved.includes(bondId))
    .map((a) => ({
      alertId: a.id,
      category: a.category,
      severity: a.severity,
      bondsInvolved: a.bondsInvolved,
      createdAt: a.createdAt,
      // Brief 2A r7b Fatia 1 — backend expõe canonicamente §32.4 (ADR-117).
      parties: a.parties,
      detectionContext: a.detectionContext,
      rationale: a.rationale,
    }));

  return { status: 'ready', alerts };
}
